/**
 * validationRules.js
 * Reglas de validación del formulario de residentes.
 */

export const REGEX_PLANO_Z  = /^Z-0000000-\d{4}$/;   // código automático para filiales sin plano
export const REGEX_EMAIL    = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const REGEX_TEL_CR   = /^[2-9]\d{3}[\s-]?\d{4}$|^\+?[\d\s-]{7,20}$/;
export const REGEX_CEDULA_CR= /^\d{9,10}$/;
// Placa CR: letras + números (ABC-123, CL-123456, MOT-1234) o solo números (123456).
// Debe coincidir con REGEX_PLACA_CR del backend (residente_completo.py).
export const REGEX_PLACA_CR = /^([A-Za-z]{1,3}[-\s]?\d{3,6}|\d{1,6})$/;

function isEmpty(v) { return !v || String(v).trim() === ""; }

// ── Cédula de Costa Rica por tipo ────────────────────────────────────────────
// Devuelve un mensaje de error o null si es válida. Solo valida formato cuando
// se conoce el tipo; PASAPORTE/Otro acepta cualquier valor no vacío.
export function validateCedulaCR(cedula, tipo) {
  const raw = String(cedula || "").replace(/[\s-]/g, "");
  switch (tipo) {
    case "FISICA":
      return /^\d{9}$/.test(raw) ? null : "Cédula física: 9 dígitos (ej: 1-1234-5678).";
    case "JURIDICA":
      return /^\d{10}$/.test(raw) ? null : "Cédula jurídica: 10 dígitos (ej: 3-101-123456).";
    case "DIMEX":
      return /^\d{11,12}$/.test(raw) ? null : "DIMEX: 11 o 12 dígitos.";
    case "NITE":
      return /^\d{10}$/.test(raw) ? null : "NITE: 10 dígitos.";
    case "PASAPORTE":
    case "":
    case undefined:
      return null; // sin tipo conocido, no se valida el formato
    default:
      return null;
  }
}

// ── Filial ────────────────────────────────────────────────────────────────────
export function validateFilial(filial) {
  const errors = {};

  if (isEmpty(filial.idFilial))
    errors.idFilial = "El ID de Filial es obligatorio.";

  // Plano y folio son texto libre: hay planos y folios reales que no siguen
  // el formato típico. El plano es opcional (o código automático Z-).
  if (isEmpty(filial.numFolio))
    errors.numFolio = "El Número de Folio es obligatorio.";

  if (!filial.tipoPropiedad || filial.tipoPropiedad.trim() === "")
    errors.tipoPropiedad = "Seleccioná el tipo de propiedad.";

  return errors;
}

// ── Filial en modo cliente ──────────────────────────────────────────────────
// En modo cliente la "filial" es solo la identidad de cobro del cliente: basta
// con un ID/código. No aplican plano, folio ni tipo de propiedad.
export function validateFilialCliente(filial) {
  const errors = {};
  if (isEmpty(filial.idFilial))
    errors.idFilial = "El ID / código del cliente es obligatorio.";
  return errors;
}

// ── Cliente: paso Identificación (identidad + dirección, sin email) ──────────
export function validateClienteIdentidad(p) {
  const errors = {};
  if (isEmpty(p.cedula)) {
    errors.cedula = "La cédula es obligatoria.";
  } else {
    const cedulaErr = validateCedulaCR(p.cedula, p.tipo_cedula);
    if (cedulaErr) errors.cedula = cedulaErr;
  }
  const esSociedad = !isEmpty(p.nombre_sociedad);
  if (!esSociedad && isEmpty(p.nombre))
    errors.nombre = "Ingresá el nombre del cliente o la razón social.";
  return errors;
}

// ── Cliente: paso Contacto (email obligatorio, teléfonos opcionales) ──────────
export function validateClienteContacto(p) {
  const errors = {};
  if (isEmpty(p.emailPrincipal))
    errors.emailPrincipal = "El email principal es obligatorio.";
  else if (!REGEX_EMAIL.test(p.emailPrincipal.trim()))
    errors.emailPrincipal = "El email no tiene un formato válido.";

  if (p.emailExtras?.length) {
    const emailErrors = [];
    let hasEmailError = false;
    p.emailExtras.forEach((e, i) => {
      if (e.email && !REGEX_EMAIL.test(e.email.trim())) {
        emailErrors[i] = `Email ${i + 2}: formato inválido.`;
        hasEmailError = true;
      }
    });
    if (hasEmailError) errors.emailExtras = emailErrors;
  }
  return errors;
}

// ── Vehículos ────────────────────────────────────────────────────────────────
// Devuelve un array de mensajes (uno por placa, null si está bien) o null si
// todas están bien. Las placas vacías se ignoran al guardar.
export function validateVehiculos(vehiculos) {
  if (!vehiculos?.length) return null;
  let hay = false;
  const vistos = new Set();
  const errs = vehiculos.map((v) => {
    const placa = String(v.placa || "").trim().toUpperCase();
    if (!placa) return null;
    if (vistos.has(placa)) { hay = true; return "Esta placa está repetida."; }
    vistos.add(placa);
    if (!v.extranjera && !REGEX_PLACA_CR.test(placa)) {
      hay = true;
      return "Formato CR: ABC-123 o 123456. Si es extranjera, marcá la casilla.";
    }
    return null;
  });
  return hay ? errs : null;
}

// Persona física (nombre + apellidos) o sociedad (nombre_sociedad), según el switch.
function validatePersonaOSociedad(p, errors, { exigirApellidos = false } = {}) {
  if (p.es_sociedad) {
    if (isEmpty(p.nombre_sociedad))
      errors.nombre_sociedad = "El nombre de la sociedad es obligatorio.";
  } else {
    if (isEmpty(p.nombre)) errors.nombre = "El nombre es obligatorio.";
    if (exigirApellidos && isEmpty(p.apellidos))
      errors.apellidos = "Los apellidos son obligatorios.";
  }
}

// ── Propietario ───────────────────────────────────────────────────────────────
export function validatePropietario(propietario) {
  const errors = {};

  // Cédula — obligatoria
  if (isEmpty(propietario.cedula)) {
    errors.cedula = "La cédula es obligatoria.";
  } else {
    // Validación de formato por tipo (solo aplica si se eligió un tipo de cédula)
    const cedulaErr = validateCedulaCR(propietario.cedula, propietario.tipo_cedula);
    if (cedulaErr) errors.cedula = cedulaErr;
  }

  validatePersonaOSociedad(propietario, errors);

  // Email principal — obligatorio salvo que se marque "No tiene correo"
  if (!propietario.sin_correo) {
    if (isEmpty(propietario.emailPrincipal))
      errors.emailPrincipal = "El email principal es obligatorio. Si no tiene, marcá \"No tiene correo\".";
    else if (!REGEX_EMAIL.test(propietario.emailPrincipal.trim()))
      errors.emailPrincipal = "El email no tiene un formato válido.";

    // Emails extra — solo validar formato si están llenos
    if (propietario.emailExtras?.length) {
      const emailErrors = [];
      let hasEmailError = false;
      propietario.emailExtras.forEach((e, i) => {
        if (e.email && !REGEX_EMAIL.test(e.email.trim())) {
          emailErrors[i] = `Email ${i + 2}: formato inválido.`;
          hasEmailError = true;
        }
      });
      if (hasEmailError) errors.emailExtras = emailErrors;
    }
  }

  const vehErr = validateVehiculos(propietario.vehiculos);
  if (vehErr) errors.vehiculos = vehErr;

  return errors;
}

// ── Copropietario ─────────────────────────────────────────────────────────────
export function validateCopropietario(co) {
  const errors = {};

  if (isEmpty(co.cedula))
    errors.cedula = "La cédula es obligatoria.";

  validatePersonaOSociedad(co, errors, { exigirApellidos: true });

  // Teléfonos — todos opcionales ("-" significa que no tiene)
  for (const [field, label] of [
    ['telefono_casa', 'Tel. Casa'],
    ['telefono_cel',  'Tel. Celular'],
    ['telefono_trabajo', 'Tel. Trabajo'],
  ]) {
    const val = co[field];
    if (!isEmpty(val) && String(val).trim() !== "-" && !co.telefonoExtranjero) {
      if (!REGEX_TEL_CR.test(String(val).replace(/\s/g, '')))
        errors[field] = `${label}: formato CR inválido (ej: 8888-1234).`;
    }
  }

  const vehErr = validateVehiculos(co.vehiculos);
  if (vehErr) errors.vehiculos = vehErr;

  return errors;
}

// ── Autorizado ────────────────────────────────────────────────────────────────
export function validateAutorizado(auth) {
  const errors = {};

  if (isEmpty(auth.cedula))
    errors.cedula = "La cédula es obligatoria.";

  if (isEmpty(auth.nombre))
    errors.nombre = "El nombre es obligatorio.";

  if (isEmpty(auth.apellidos))
    errors.apellidos = "Los apellidos son obligatorios.";

  const vehErr = validateVehiculos(auth.vehiculos);
  if (vehErr) errors.vehiculos = vehErr;

  return errors;
}
